import { initializeApp, FirebaseApp } from 'firebase/app';
import { getAnalytics, Analytics, isSupported } from 'firebase/analytics';
import { FirebaseAnalyticsConfig } from '../types/analytics';

// Firebase configuration for the sailing-handicap-calculator project
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'sailing-handicap-calculator.firebaseapp.com',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'sailing-handicap-calculator',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'sailing-handicap-calculator.appspot.com',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Analytics configuration with defaults
const analyticsConfig: FirebaseAnalyticsConfig = {
  measurementId: firebaseConfig.measurementId || '',
  appId: firebaseConfig.appId || '',
  dataRetentionDays: 26 * 30, // 26 months default
  automaticDataCollection: true,
  cookieSettings: {
    cookiePrefix: '_ga',
    cookieDomain: 'auto',
    cookieExpires: 63072000, // 2 years in seconds
  },
};

// Initialize Firebase
const app: FirebaseApp = initializeApp(firebaseConfig);

// Initialize Analytics with proper checks
let analytics: Analytics | null = null;

/**
 * Initialize Firebase Analytics with proper environment and support checks
 * @return {Promise<Analytics | null>} Analytics instance or null if initialization fails
 */
export const initializeAnalytics = async (): Promise<Analytics | null> => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    console.warn('Firebase Analytics: Not in browser environment');
    return null;
  }

  // Check if measurement ID is configured
  if (!firebaseConfig.measurementId) {
    console.warn('Firebase Analytics: Measurement ID not configured');
    return null;
  }

  try {
    // Check if Analytics is supported in this environment
    const supported = await isSupported();
    if (!supported) {
      console.warn('Firebase Analytics: Not supported in this environment');
      return null;
    }

    // Initialize Analytics
    analytics = getAnalytics(app);
    console.log('Firebase Analytics initialized successfully');
    return analytics;
  } catch (error) {
    console.error('Firebase Analytics initialization failed:', error);
    return null;
  }
};

/**
 * Get the current Analytics instance
 * @return {Analytics | null} Current analytics instance or null
 */
export const getAnalyticsInstance = (): Analytics | null => {
  return analytics;
};

/**
 * Check if Analytics is available and initialized
 * @return {boolean} True if analytics is available, false otherwise
 */
export const isAnalyticsAvailable = (): boolean => {
  return analytics !== null;
};

export { app, analytics, analyticsConfig };
export default app;
